import React, { useState, useEffect, useRef } from 'react';
import { callFunctionWithFallback } from '../firebase';
import { Bot, User, X, Send, Loader2 } from 'lucide-react';

interface AdminAssistantModalProps {
  onClose: () => void;
}

interface Message {
  role: 'user' | 'assistant';
  text: string;
}

export const AdminAssistantModal: React.FC<AdminAssistantModalProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', text: 'Hello! I am your AI Administrative Assistant. I am analyzing the platform data now... How can I help you today?' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const adminDataAssistant = (data: any) => callFunctionWithFallback('adminDataAssistant', data);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const startSession = async () => {
      try {
        const result = await adminDataAssistant({ action: "START" }) as { data: { chatSessionId: string } };
        setChatSessionId(result.data.chatSessionId);
        setMessages([
          { role: 'assistant', text: 'Data analysis complete! I am ready to answer your questions about user statistics, performance trends, and stored sessions.' }
        ]);
      } catch (error) {
        console.error("Failed to start admin assistant session:", error);
        setMessages([
          { role: 'assistant', text: 'Sorry, I encountered an error initializing the data analysis. You can try asking a question, but I may not have full context.' }
        ]);
      } finally {
        setIsInitializing(false);
      }
    };
    startSession();
  }, []);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || !chatSessionId || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setIsLoading(true);

    try {
      const result = await adminDataAssistant({ 
        action: "CHAT", 
        chatSessionId, 
        message: userMessage 
      }) as { data: { text: string } };

      setMessages(prev => [...prev, { role: 'assistant', text: result.data.text }]);
    } catch (error: any) {
      console.error("Assistant Error:", error);
      setMessages(prev => [...prev, { role: 'assistant', text: `An error occurred: ${error.message}` }]);
    } finally {
      setIsLoading(false);
    }
  };

  /* Floating Widget Styles */
  const floatingWidgetStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: '2rem',
    right: '2rem',
    zIndex: 9999,
    background: '#ffffff', 
    border: '1px solid #E5E7EB',
    width: '400px', 
    borderRadius: '1rem',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)', 
    display: 'flex', 
    flexDirection: 'column',
    height: '600px', 
    maxHeight: 'calc(100vh - 4rem)', 
    overflow: 'hidden',
    animation: 'fadeIn 0.2s ease-out'
  };

  return (
    <div style={floatingWidgetStyle}>
        
        {/* Header */}
        <div style={{ background: '#FAFAFA', padding: '1rem 1.25rem', borderBottom: '1px solid #F1F3F5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ background: 'var(--primary)', padding: '0.5rem', borderRadius: '0.5rem' }}>
              <Bot size={20} color="white" />
            </div>
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, color: '#111827' }}>Simulate AI Assistant</h2>
              <p style={{ fontSize: '0.75rem', color: '#6B7280', margin: 0 }}>Admin Data Insights & Analysis</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.5rem', borderRadius: '50%', color: '#9CA3AF' }}
            onMouseEnter={e => e.currentTarget.style.color = '#111827'}
            onMouseLeave={e => e.currentTarget.style.color = '#9CA3AF'}
          >
            <X size={24} />
          </button>
        </div>

        {/* Chat Area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', background: '#ffffff' }}>
          {messages.map((msg, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '0.75rem', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              
              {msg.role === 'assistant' && (
                <div style={{ width: '2rem', height: '2rem', borderRadius: '50%', background: 'rgba(160,43,147,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Bot size={16} color="var(--primary)" />
                </div>
              )}

              <div style={{
                maxWidth: '80%', padding: '0.85rem 1rem', fontSize: '0.9rem', lineHeight: '1.5',
                borderRadius: '1rem',
                borderTopRightRadius: msg.role === 'user' ? '0.2rem' : '1rem',
                borderTopLeftRadius: msg.role === 'assistant' ? '0.2rem' : '1rem',
                background: msg.role === 'user' ? 'var(--primary)' : '#F3F4F6',
                color: msg.role === 'user' ? 'white' : '#1F2937',
              }}>
                <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
              </div>

              {msg.role === 'user' && (
                <div style={{ width: '2rem', height: '2rem', borderRadius: '50%', background: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <User size={16} color="#4F46E5" />
                </div>
              )}
            </div>
          ))}
          
          {(isLoading || isInitializing) && (
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-start' }}>
              <div style={{ width: '2rem', height: '2rem', borderRadius: '50%', background: 'rgba(160,43,147,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Loader2 size={16} color="var(--primary)" className="animate-spin" />
              </div>
              <div style={{ background: '#F3F4F6', borderRadius: '1rem', borderTopLeftRadius: '0.2rem', padding: '0.85rem 1rem', display: 'flex', alignItems: 'center' }}>
                <span style={{ fontSize: '0.85em', color: '#6B7280' }}>
                  {isInitializing ? 'Analyzing database...' : 'Generating insights...'}
                </span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div style={{ padding: '1rem', borderTop: '1px solid #F1F3F5', background: '#FAFAFA' }}>
          <form onSubmit={handleSend} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isInitializing ? "Initializing data context..." : "Ask me about users, scores, trends..."}
              disabled={isLoading || isInitializing || !chatSessionId}
              style={{
                width: '100%', padding: '0.85rem 3.5rem 0.85rem 1rem', borderRadius: '0.75rem',
                border: '1px solid #D1D5DB', fontSize: '0.9rem',
                outline: 'none', transition: 'all 0.2s', ...(isLoading || isInitializing ? { opacity: 0.6 } : {})
              }}
            />
            <button
              type="submit"
              disabled={!input.trim() || isLoading || isInitializing || !chatSessionId}
              style={{
                position: 'absolute', right: '0.4rem', padding: '0.5rem', borderRadius: '0.5rem', border: 'none',
                background: (!input.trim() || isLoading || isInitializing || !chatSessionId) ? '#D1D5DB' : 'var(--primary)',
                color: 'white', cursor: (!input.trim() || isLoading || isInitializing || !chatSessionId) ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
              }}
            >
              <Send size={18} />
            </button>
          </form>
        </div>

      </div>
  );
};
